import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, MessageCircle, Loader2, Save, StickyNote } from "lucide-react";
import { DashShell, Badge } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LayoutDashboard } from "lucide-react";

const BDE_NAV = [{ to: "/bde", label: "My Leads", icon: LayoutDashboard }];

export default function LeadDetail() {
    const { leadId } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const isBde = user?.role === "bde";
    const [data, setData] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [bdes, setBdes] = useState([]);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        api.get(`/admin/leads/${leadId}`).then(({ data }) => setData(data)).catch(() => { toast.error("Not found"); nav(-1); });
    }, [leadId, nav]);

    useEffect(() => {
        load();
        api.get("/meta").then(({ data }) => setStatuses(data.lead_statuses));
        if (!isBde) api.get("/admin/bdes").then(({ data }) => setBdes(data));
    }, [load, isBde]);

    const update = async (patch) => {
        setSaving(true);
        try {
            await api.patch(`${isBde ? "/bde" : "/admin"}/leads/${leadId}`, patch);
            toast.success("Updated");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        try { await api.post(`/admin/leads/${leadId}/notes`, { text: note }); setNote(""); load(); toast.success("Note added"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const shell = (children) => <DashShell nav={isBde ? BDE_NAV : ADMIN_NAV} title="Lead Detail">{children}</DashShell>;
    if (!data) return shell(<div className="grid place-items-center py-32 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div>);
    const l = data.lead;

    return shell(
        <div>
            <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-teal-900/60 hover:text-teal-900 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Panel title="Customer Information">
                        <Grid items={[["Name", l.customer_name], ["Business", l.business_name || "—"], ["Mobile", l.mobile], ["WhatsApp", l.whatsapp || "—"], ["Email", l.email || "—"], ["City", l.city || "—"], ["Category", l.business_category || "—"], ["Source", l.source]]} />
                        <div className="flex gap-2 mt-4">
                            <a href={`tel:${l.mobile}`} className="btn-ghost-cream !border-teal-900/20 !text-teal-900 rounded-full px-4 py-2 text-sm flex items-center gap-1.5" data-testid="lead-call"><Phone className="w-4 h-4" /> Call</a>
                            <a href={`https://wa.me/91${(l.whatsapp || l.mobile || "").replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer" className="btn-ghost-cream !border-teal-900/20 !text-teal-900 rounded-full px-4 py-2 text-sm flex items-center gap-1.5" data-testid="lead-whatsapp"><MessageCircle className="w-4 h-4 text-growth" /> WhatsApp</a>
                        </div>
                    </Panel>

                    <Panel title="Requirement">
                        <Grid items={[["Service", l.service || "—"], ["Package", l.package || "—"], ["Amount", l.amount ? inr(l.amount) : "—"], ["Website", l.website || "—"]]} />
                        {l.message && <p className="mt-3 text-sm text-teal-900/70 bg-teal-900/5 rounded-lg p-3">{l.message}</p>}
                        {data.order?.additional_requirements && <p className="mt-3 text-sm text-teal-900/70 bg-teal-900/5 rounded-lg p-3">{data.order.additional_requirements}</p>}
                    </Panel>

                    <Panel title="Communication History" icon={StickyNote}>
                        <div className="flex gap-2 mb-4">
                            <Input placeholder="Add a note / call log…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="note-input" />
                            <button onClick={addNote} className="btn-gold rounded-full px-4 py-2 text-sm" data-testid="note-add">Add</button>
                        </div>
                        <div className="space-y-3">
                            {data.notes.map((n, i) => (
                                <div key={i} className="border-l-2 border-gold pl-3 py-1">
                                    <div className="text-sm text-teal-900">{n.text}</div>
                                    <div className="text-xs text-teal-900/40">{n.author} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                                </div>
                            ))}
                            {!data.notes.length && <p className="text-sm text-teal-900/40">No notes yet.</p>}
                        </div>
                    </Panel>
                </div>

                <div className="space-y-6">
                    <Panel title="Status">
                        <div className="flex items-center gap-2 mb-3"><Badge status={l.lead_status} /> <Badge status={l.payment_status} /></div>
                        <label className="text-xs text-teal-900/60">Lead Status</label>
                        <select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm mt-1" value={l.lead_status} onChange={(e) => update({ lead_status: e.target.value })} data-testid="lead-status-select">
                            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <label className="text-xs text-teal-900/60 mt-3 block">Next Follow-up</label>
                        <Input type="date" className="mt-1" defaultValue={l.next_followup || ""} onChange={(e) => update({ next_followup: e.target.value })} data-testid="followup-date" />
                    </Panel>

                    {!isBde && (
                        <Panel title="Assign BDE">
                            <select className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm" value={l.assigned_bde_id || ""} onChange={(e) => update({ assigned_bde_id: e.target.value })} data-testid="assign-bde-select">
                                <option value="">Unassigned</option>
                                {bdes.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                            {l.assigned_bde_name && <p className="text-sm text-teal-900/60 mt-2">Assigned to <span className="font-semibold">{l.assigned_bde_name}</span></p>}
                        </Panel>
                    )}
                    {saving && <div className="flex items-center gap-2 text-xs text-teal-900/50"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}
                </div>
            </div>
        </div>
    );
}

function Panel({ title, icon: Icon, children }) {
    return <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm"><h3 className="font-semibold text-teal-900 mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-gold-amber" />}{title}</h3>{children}</div>;
}
function Grid({ items }) {
    return <div className="grid grid-cols-2 gap-x-4 gap-y-3">{items.map(([k, v]) => <div key={k}><div className="text-xs text-teal-900/50 uppercase">{k}</div><div className="text-sm text-teal-900 mt-0.5 break-words">{v}</div></div>)}</div>;
}
